/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useNavigate } from 'js/lib/useRouter';

import { Grid, PageGridContainer, Typography, breakpoints } from '@coursera/cds-core';
import { ReadMore, color } from '@coursera/coursera-ui';
import { SvgChevronDown, SvgChevronUp } from '@coursera/coursera-ui/svg';

import BrowseBreadcrumbs from 'bundles/browse/components/Domain/BrowseBreadcrumbs';
import SubdomainCarousel from 'bundles/browse/components/Domain/SubdomainCarousel';
import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import type { Subdomain } from 'bundles/browse/components/Domain/types/SubDomain';
import { BANNER_HEIGHT } from 'bundles/browse/constants';

import _t from 'i18n!nls/browse';

const styles = {
  subdomainBanner: css`
    padding: 32px 0 12px;
    min-height: ${BANNER_HEIGHT}px;
    background: linear-gradient(135deg, #011d4a 0%, #448ecb 100%);
    ${breakpoints.down('sm')} {
      max-height: 480px;
    }
    ${breakpoints.down('xs')} {
      padding: 16px 0 0;
    }

    .subdomain-carousel {
      margin-top: 0;
      margin-bottom: 20px;
    }

    .subdomain-name {
      color: white;
      padding: 12px 0;
      ${breakpoints.down('xs')} {
        padding: 0;
      }
    }

    .subdomain-description,
    span {
      color: white;
      max-width: 1000px;
    }

    button {
      display: block;

      .subdomain-description-toggle {
        color: white;
        margin-top: 5px;
        text-transform: none;
        font-weight: 600;
      }
    }

    .subdomain-grid,
    .subdomain-carousel-wrapper {
      width: 100%;
    }

    .rc-BrowseCarousel {
      .slick-list,
      .slick-track {
        min-width: 100%;
      }
    }
  `,
};

type PropsFromCaller = {
  subdomain: Subdomain;
  domain: Domain;
  /**
   * A callback function for generating the domain page url.
   */
  onGetSubdomainPageUrl?: (domain: Domain, subdomain: Subdomain) => string;

  /**
   * Custom domain card click handler
   */
  onClick?: (url: string, domain: Domain, subdomain: Subdomain) => void;
};

const svgStyle = {
  fill: color.white,
  position: 'relative' as const, // see: https://github.com/microsoft/TypeScript/issues/11465#issuecomment-252453037
  bottom: '2px',
};

const showAll = () => (
  <p className="subdomain-description-toggle">
    {_t('SHOW ALL')} <SvgChevronDown style={svgStyle} size={14} />
  </p>
);

const showLess = () => (
  <p className="subdomain-description-toggle">
    {_t('SHOW LESS')} <SvgChevronUp style={svgStyle} size={14} />
  </p>
);

export const SubdomainsBanner = ({ subdomain, domain, onClick, onGetSubdomainPageUrl }: PropsFromCaller) => {
  const navigate = useNavigate();
  const currentSubdomainId = subdomain.id;
  const relatedSubdomains = domain.subdomains?.elements.filter(({ id }) => id !== currentSubdomainId);

  const getSubdomainPageUrl = (targetSubdomain: Subdomain) => {
    if (onGetSubdomainPageUrl) {
      return onGetSubdomainPageUrl(domain, targetSubdomain);
    } else {
      return `/browse/${domain.slug || domain.id}/${targetSubdomain.id}`;
    }
  };

  const handleClick = (targetSubdomain: Subdomain) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    const url = getSubdomainPageUrl(targetSubdomain);

    event.preventDefault();

    if (onClick) {
      onClick(url, domain, targetSubdomain);
    } else {
      navigate(url);
    }
  };

  return (
    <div className="rc-SubdomainsBanner" data-testid="subdomains-banner" css={styles.subdomainBanner}>
      <PageGridContainer maxWidth="lg">
        <Grid className="subdomain-grid" sm={12} direction="column">
          <BrowseBreadcrumbs domain={domain} subdomain={subdomain} />
          <Typography variant="d2" component="h1" className="subdomain-name" data-e2e="subdomain-name">
            {subdomain.name}
          </Typography>
          <p className="subdomain-description">
            <ReadMore more={showAll()} less={showLess()}>
              {subdomain.description}
            </ReadMore>
          </p>
          <div className="subdomain-carousel-wrapper">
            {relatedSubdomains?.length > 1 && (
              <SubdomainCarousel
                subdomains={relatedSubdomains}
                domain={domain}
                handleSubdomainClick={handleClick}
                onGetSubdomainPageUrl={onGetSubdomainPageUrl}
              />
            )}
          </div>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default SubdomainsBanner;
